import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems, buildSelectFields,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  API_BASE, INVOICE_VARIANTS, COLUMN_FILTER_META, SEARCH_FIELDS,
  lifecycleLabel, lifecycleTone, approvalLabel, approvalTone, toDecimal, formatMoney, displayDate,
  normalizeGuid, pickValue, lookupLabel, resolvePaymentTypeCode, invoiceBasePath,
} from './invoiceShared';

// detailBase: detay ekranının rota öneki. Varsayılan = varyantın kendi rotası. "Fatura V2"
// prototipi (/invoices-v2) aynı listeyi kullanıp detayı V2 ekranına yönlendirmek için verir.
export function InvoicesPage({ variant = 'sales', detailBase = null } = {}) {
  const cfg = INVOICE_VARIANTS[variant] || INVOICE_VARIANTS.sales;
  usePageMeta(cfg.title, cfg.subtitle);
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // §6.1-6.3 — belgeye bağlı tahsilat varsa "sessiz iptal yasak": karar diyaloğu (bağlı tahsilat listesi).
  const [collDecision, setCollDecision] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Lookup'lar (kolon etiketleri için).
  const [representatives, setRepresentatives] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [customerLabels, setCustomerLabels] = useState({}); // id -> label (lazy)
  const [paymentsByInvoice, setPaymentsByInvoice] = useState({});
  const [paymentPopup, setPaymentPopup] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: yalnız görünür kolonların OData alanları + kritikler. customerLabel/representativeLabel/
  // paymentTypeLabel display kolonları row'dan CustomerId/RepresentativeId/PaymentType okur (COLUMN_FILTER_META'da
  // yok) → always'te. paymentProgress display kolonu NetAmount'u kolon gizliyken de okur; popup TextId/PrintedNo
  // gösterir → always'te. sortField $select'e katılmaz (orderby projeksiyondan önce tam entity'ye uygulanır).
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'CustomerId', 'RepresentativeId', 'PaymentType', 'NetAmount', 'TextId', 'PrintedNo'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'DocumentDate',
    defaultSortDir: 'desc',
    // Menü konumuna göre Tür filtresi (Satış=0 / Alış=1 / İade=2,3,4).
    baseFilter: cfg.baseFilter,
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  // Küçük lookup'ları yükle.
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((res) => res.ok ? res.json() : []),
      fetch('/api/paymenttypes/Select', { headers }).then((res) => res.ok ? res.json() : []),
    ]).then(([representativeData, paymentTypeData]) => {
      setRepresentatives(unwrapLookupItems(representativeData));
      setPaymentTypes(unwrapLookupItems(paymentTypeData));
    }).catch(() => {});
  }, []);

  const representativeName = useMemo(() => {
    const map = new Map(representatives.map((r) => [r.id, lookupLabel(r)]));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [representatives]);

  const customerName = useCallback((id) => (id ? (customerLabels[id] || '…') : '-'), [customerLabels]);

  const paymentTypeIdByCode = useCallback((code) => {
    const normalizedCode = Number.parseInt(code, 10);
    if (Number.isNaN(normalizedCode)) return '';
    const item = paymentTypes.find((entry) => resolvePaymentTypeCode(entry) === normalizedCode);
    return item?.id || '';
  }, [paymentTypes]);
  const getPaymentTypeLabel = useCallback((id) => {
    const item = paymentTypes.find((p) => p.id === id);
    return item ? lookupLabel(item) : '-';
  }, [paymentTypes]);

  // Müşteri etiketlerini id bazında lazy çek (4000+ müşteri → tümü yüklenmez).
  const custFetchRef = useRef(new Set());
  const ensureCustomerLabels = useCallback(async (ids) => {
    const need = [...new Set(ids.filter((id) => id && customerLabels[id] === undefined && !custFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => custFetchRef.current.add(id));
    try {
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/customers?$top=500&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        const found = {};
        if (res.ok) { const data = await res.json(); getODataRows(data).forEach((c) => { found[c.id] = lookupLabel(c); }); }
        setCustomerLabels((prev) => { const next = { ...prev }; chunk.forEach((id) => { next[id] = found[id] || '-'; }); return next; });
      }
    } finally {
      need.forEach((id) => custFetchRef.current.delete(id));
    }
  }, [customerLabels]);

  // Liste satırlarındaki müşteri etiketlerini lazy yükle.
  useEffect(() => {
    if (rows.length) ensureCustomerLabels(rows.map((r) => r.customerId).filter(Boolean));
  }, [rows, ensureCustomerLabels]);

  // Liste satırlarındaki ödemeleri çek (ödeme % kolonu + popup için).
  useEffect(() => {
    const ids = rows.map((row) => row.id).filter(Boolean);
    if (!ids.length) {
      setPaymentsByInvoice({});
      return undefined;
    }
    const idSet = new Set(ids.map(normalizeGuid));
    const url = `/api/payments?$top=5000&$orderby=${encodeURIComponent('PaymentDate desc')}`;
    let cancelled = false;
    fetch(url, { headers: authHeaders() })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled || !data) return;
        const payments = getODataRows(data);
        const map = {};
        payments.forEach((payment) => {
          const invoiceId = normalizeGuid(pickValue(payment, 'invoiceId', 'InvoiceId'));
          if (!invoiceId || !idSet.has(invoiceId)) return;
          if (!map[invoiceId]) map[invoiceId] = { total: 0, items: [] };
          map[invoiceId].total += toDecimal(pickValue(payment, 'amount', 'Amount'));
          map[invoiceId].items.push(payment);
        });
        setPaymentsByInvoice(map);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rows]);

  // Detay artık tam-sayfa route (/invoices/:id). Liste aksiyonları o sayfaya yönlendirir.
  // Varyantın kendi route öneki → doğru menü vurgusu (Alış'ta yeni açınca Alış menüsü aktif).
  const basePath = detailBase || invoiceBasePath(variant);
  const openNew = () => navigate(`${basePath}/new?variant=${variant}`);
  const openEdit = (row = selected) => { if (row?.id) navigate(`${basePath}/${row.id}?edit=1`); };
  const openView = (row = selected) => { if (row?.id) navigate(`${basePath}/${row.id}`); };

  const handleDelete = async (collectionAction = null) => {
    if (!selected) return;
    try {
      const qs = collectionAction ? `?collectionAction=${collectionAction}` : '';
      const res = await fetch(`${API_BASE}/Delete${qs}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const data = await res.json().catch(() => null);
      const payload = data?.data || data?.Data;
      // §6.1-6.3 — belgeye bağlı aktif tahsilat var: backend 409 + karar ister (keep/cancel).
      if (res.status === 409 && (payload?.code || payload?.Code) === 'TAHSILAT_KARARI_GEREKLI') {
        setDeleteConfirm(false);
        setCollDecision(payload.collections || payload.Collections || []);
        return;
      }
      if (!res.ok) throw new Error(data?.message || data?.Message || 'Kayit silinemedi');
      showToast('success', 'Fatura silindi');
      setDeleteConfirm(false);
      setCollDecision(null);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
      setCollDecision(null);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Fatura Kodu', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('printedNo', { header: 'Basılı No', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('documentDate', {
      header: 'Tarih', meta: { filterType: 'text' },
      cell: (info) => displayDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'customerLabel', header: 'Müşteri',
      cell: ({ row }) => customerName(row.original.customerId),
    }),
    columnHelper.display({
      id: 'representativeLabel', header: 'Temsilci',
      cell: ({ row }) => representativeName(row.original.representativeId),
    }),
    columnHelper.display({
      id: 'paymentTypeLabel', header: 'Ödeme Tipi',
      cell: ({ row }) => getPaymentTypeLabel(paymentTypeIdByCode(row.original.paymentType)),
    }),
    columnHelper.accessor('netAmount', {
      header: 'Net', meta: { filterType: 'number' },
      cell: (info) => <div style={{ width: '100%', textAlign: 'right' }}>{formatMoney(info.getValue())}</div>,
    }),
    // #257 Durum artık yaşam döngüsü (LifecycleStatus); Onay Durumu ayrı kolon.
    columnHelper.accessor('lifecycleStatus', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : <StatusBadge tone={lifecycleTone(v)}>{lifecycleLabel(v)}</StatusBadge>; },
    }),
    columnHelper.accessor('approvalStatus', {
      header: 'Onay Durumu', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : <StatusBadge tone={approvalTone(v)}>{approvalLabel(v)}</StatusBadge>; },
    }),
    columnHelper.display({
      id: 'paymentProgress', header: 'Ödeme %',
      cell: ({ row }) => {
        const net = toDecimal(row.original.netAmount);
        const info = paymentsByInvoice[normalizeGuid(row.original.id)];
        const paid = info?.total || 0;
        const rawPercent = net > 0 ? (paid / net) * 100 : 0;
        const percent = Math.max(0, Math.min(100, rawPercent));
        const displayedPercent = Math.round(rawPercent);
        const statusColor = displayedPercent >= 100 ? '#16a34a' : displayedPercent >= 50 ? '#d97706' : '#dc2626';
        const trackColor = displayedPercent >= 100 ? '#dcfce7' : displayedPercent >= 50 ? '#fef3c7' : '#fee2e2';
        return (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 160 }}>
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); setPaymentPopup({ invoice: row.original, payments: info?.items || [], total: paid }); }}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', height: 28, padding: '0 8px', border: 'none', borderRadius: 9999,
                background: trackColor, overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.28)',
                cursor: 'pointer', textAlign: 'left',
              }}
              title="Odeme detaylarini goster"
            >
              <div style={{ position: 'absolute', inset: 0, width: `${percent}%`, borderRadius: 9999, background: `linear-gradient(90deg, ${statusColor} 0%, ${statusColor}CC 100%)`, transition: 'width 0.2s ease' }} />
              <span style={{ position: 'relative', zIndex: 1, color: statusColor, fontSize: 11, fontWeight: 800, background: 'rgba(255,255,255,0.88)', border: '1px solid rgba(255,255,255,0.92)', borderRadius: 9999, padding: '1px 6px', lineHeight: 1.2 }}>
                {`%${displayedPercent}`}
              </span>
              <span style={{ position: 'relative', zIndex: 1, color: '#111827', fontSize: 11, fontWeight: 700, textShadow: '0 1px 0 rgba(255,255,255,0.35)' }}>
                {formatMoney(paid)}
              </span>
            </button>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [customerName, representativeName, getPaymentTypeLabel, paymentTypeIdByCode, paymentsByInvoice, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('DocumentDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Fatura Kodu' },
        { field: 'printedNo', headerName: 'Basılı No' },
        { headerName: 'Tarih', valueGetter: ({ row }) => displayDate(row.documentDate) },
        { headerName: 'Müşteri', valueGetter: ({ row }) => customerName(row.customerId) },
        { headerName: 'Temsilci', valueGetter: ({ row }) => representativeName(row.representativeId) },
        { headerName: 'Ödeme Tipi', valueGetter: ({ row }) => getPaymentTypeLabel(paymentTypeIdByCode(row.paymentType)) },
        { field: 'netAmount', headerName: 'Net' },
        { headerName: 'Durum', valueGetter: ({ row }) => lifecycleLabel(row.lifecycleStatus) },
        { headerName: 'Onay Durumu', valueGetter: ({ row }) => approvalLabel(row.approvalStatus) },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `faturalar_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Fatura kodu, no, ERP veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>{cfg.newLabel}</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {paymentPopup && (
        <div style={s.overlay} onClick={() => setPaymentPopup(null)}>
          <div style={{ ...s.modal, maxWidth: 780 }} onClick={(event) => event.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Fatura Odemeleri - {paymentPopup.invoice.textId || paymentPopup.invoice.printedNo || '-'}</h3>
              <button style={s.closeBtn} onClick={() => setPaymentPopup(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Fatura Toplami</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{formatMoney(paymentPopup.invoice.netAmount)}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Toplam Odeme</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{formatMoney(paymentPopup.total)}</div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: 12, fontWeight: 700 }}>Kalan</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                    {formatMoney(Math.max(0, (Number(paymentPopup.invoice.netAmount) || 0) - paymentPopup.total))}
                  </div>
                </div>
              </div>

              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginTop: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 0.9fr 1fr', gap: 8, padding: '10px 12px', background: '#f9fafb', color: '#6b7280', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>
                  <div>Odeme No</div>
                  <div>Tarih</div>
                  <div>Banka</div>
                  <div>Makbuz</div>
                  <div style={{ textAlign: 'right' }}>Tutar</div>
                </div>
                {paymentPopup.payments.length === 0 ? (
                  <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Bu faturaya ait odeme kaydi bulunmuyor.</div>
                ) : paymentPopup.payments.map((payment) => (
                  <div key={payment.id} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.1fr 1fr 0.9fr 1fr', gap: 8, padding: '12px', borderTop: '1px solid #e5e7eb', fontSize: 13, color: '#111827' }}>
                    <div>{payment.textId || '-'}</div>
                    <div>{displayDate(payment.paymentDate)}</div>
                    <div>{payment.bankName || '-'}</div>
                    <div>{payment.receiptNo || '-'}</div>
                    <div style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(payment.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setPaymentPopup(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>Bu faturayı silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}><button style={s.btnDanger} onClick={() => handleDelete()}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div>
          </div>
        </div>
      )}

      {collDecision && (
        <div style={s.overlay} onClick={() => setCollDecision(null)}>
          <div style={{ ...s.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Bağlı Tahsilat Var</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: '0 0 10px', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                Bu belge aşağıdaki tahsilat(lar) ile kapatılmış (ilişkilendirilmiş). Tahsilat kaydını da iptale almak ister misiniz?
              </p>
              <ul style={{ margin: '0 0 4px', paddingLeft: 18, color: 'var(--text-primary)' }}>
                {collDecision.map((c, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {(c.documentNo || c.DocumentNo) ? `${c.documentNo || c.DocumentNo} — ` : ''}
                    {Number(c.amount ?? c.Amount ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </li>
                ))}
              </ul>
              <p style={{ margin: '8px 0 0', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 }}>
                <strong>Hayır</strong> derseniz belge silinir, tahsilat ayakta kalır ve avans (mahsupsuz) tahsilata döner — başka faturaya mahsup edilebilir.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete('cancel')}>Evet, tahsilat da iptal edilsin</button>
              <button style={s.btnSecondary} onClick={() => handleDelete('keep')}>Hayır, yalnız belgeyi sil</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
